import React, { useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MarginEntry {
  id: string;
  counterparty: string;
  portfolio: string;
  im_required: number;
  im_posted: number;
  vm_receivable: number;
  vm_payable: number;
  marginCall: number | null;
  callDue: string | null;
  collateral: string;
  csa: string;
  status: 'Settled' | 'Pending' | 'Disputed' | 'Call Issued';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MARGIN_DATA: MarginEntry[] = [
  {
    id: 'm1',
    counterparty: 'Goldman Sachs',
    portfolio: 'IRS Book A',
    im_required: 45.2,
    im_posted: 46.0,
    vm_receivable: 12.4,
    vm_payable: 0,
    marginCall: null,
    callDue: null,
    collateral: 'US Treasuries',
    csa: 'ISDA 2002',
    status: 'Settled',
  },
  {
    id: 'm2',
    counterparty: 'JPMorgan',
    portfolio: 'IRS Book B',
    im_required: 38.5,
    im_posted: 38.5,
    vm_receivable: 0,
    vm_payable: 8.7,
    marginCall: 8.7,
    callDue: '14:00 HKT',
    collateral: 'Cash USD',
    csa: 'CSA 2016 VM',
    status: 'Call Issued',
  },
  {
    id: 'm3',
    counterparty: 'Deutsche Bank',
    portfolio: 'CDS Portfolio',
    im_required: 62.0,
    im_posted: 55.0,
    vm_receivable: 0,
    vm_payable: 15.3,
    marginCall: 22.3,
    callDue: '12:00 HKT',
    collateral: 'US Treasuries',
    csa: 'ISDA 2002',
    status: 'Disputed',
  },
  {
    id: 'm4',
    counterparty: 'HSBC HK',
    portfolio: 'FX Options Book',
    im_required: 22.1,
    im_posted: 22.1,
    vm_receivable: 5.6,
    vm_payable: 0,
    marginCall: null,
    callDue: null,
    collateral: 'Gilts',
    csa: 'CSA 2016 VM',
    status: 'Settled',
  },
  {
    id: 'm5',
    counterparty: 'Citadel',
    portfolio: 'CDS Book A',
    im_required: 18.8,
    im_posted: 19.0,
    vm_receivable: 3.2,
    vm_payable: 0,
    marginCall: null,
    callDue: null,
    collateral: 'Cash USD',
    csa: 'ISDA 2002',
    status: 'Settled',
  },
  {
    id: 'm6',
    counterparty: 'BlackRock',
    portfolio: 'IRS Book C',
    im_required: 31.4,
    im_posted: 31.4,
    vm_receivable: 0,
    vm_payable: 6.8,
    marginCall: 6.8,
    callDue: '16:30 HKT',
    collateral: 'US Treasuries',
    csa: 'CSA 2016 VM',
    status: 'Pending',
  },
  {
    id: 'm7',
    counterparty: 'Bank of China',
    portfolio: 'IRS Book D',
    im_required: 28.6,
    im_posted: 28.6,
    vm_receivable: 9.1,
    vm_payable: 0,
    marginCall: null,
    callDue: null,
    collateral: 'Gilts',
    csa: 'ISDA 2002',
    status: 'Settled',
  },
  {
    id: 'm8',
    counterparty: 'Macquarie',
    portfolio: 'Mixed Book',
    im_required: 14.2,
    im_posted: 14.2,
    vm_receivable: 0,
    vm_payable: 2.1,
    marginCall: null,
    callDue: null,
    collateral: 'Cash USD',
    csa: 'ISDA 2002',
    status: 'Settled',
  },
];

const COLLATERAL_BREAKDOWN = [
  { name: 'US Treasuries', pct: 45, color: 'bg-rose-500' },
  { name: 'Cash USD', pct: 30, color: 'bg-blue-500' },
  { name: 'Gilts', pct: 15, color: 'bg-emerald-500' },
  { name: 'Other', pct: 10, color: 'bg-amber-500' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusStyle(s: MarginEntry['status']): { bg: string; text: string } {
  switch (s) {
    case 'Settled': return { bg: 'bg-emerald-100', text: 'text-emerald-700' };
    case 'Pending': return { bg: 'bg-amber-100', text: 'text-amber-700' };
    case 'Disputed': return { bg: 'bg-red-100', text: 'text-red-700' };
    case 'Call Issued': return { bg: 'bg-rose-100', text: 'text-rose-700' };
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MarginDashboardView() {
  const [selected, setSelected] = useState<MarginEntry | null>(null);

  const totalIM = MARGIN_DATA.reduce((s, m) => s + m.im_posted, 0);
  const vmNet = MARGIN_DATA.reduce((s, m) => s + m.vm_receivable - m.vm_payable, 0);
  const openCalls = MARGIN_DATA.filter(m => m.marginCall !== null && m.status !== 'Settled').length;
  const disputed = MARGIN_DATA.filter(m => m.status === 'Disputed').length;
  const pendingCalls = MARGIN_DATA.filter(m => m.marginCall !== null);

  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Margin & Collateral Dashboard</h1>
            <p className="text-xs text-slate-500">IM / VM Tracking for OTC Derivatives — Type 11 SFC</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-400">As of:</span>
            <span className="text-xs font-medium text-slate-600">2026-02-20 09:15 HKT</span>
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* Stat Cards */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Total IM Posted" value={`$${totalIM.toFixed(1)}M`} sub="Across all portfolios" color="rose" />
          <StatCard
            label="VM Net Position"
            value={`${vmNet >= 0 ? '+' : ''}$${vmNet.toFixed(1)}M`}
            sub={vmNet >= 0 ? 'Net receivable' : 'Net payable'}
            color={vmNet >= 0 ? 'green' : 'amber'}
          />
          <StatCard
            label="Open Margin Calls"
            value={String(openCalls)}
            sub="Require action today"
            color={openCalls > 0 ? 'red' : 'green'}
            highlight={openCalls > 0}
          />
          <StatCard
            label="Disputed Calls"
            value={String(disputed)}
            sub="Under investigation"
            color={disputed > 0 ? 'amber' : 'green'}
          />
        </div>

        {/* Today's Margin Calls */}
        {pendingCalls.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Today's Margin Calls
            </div>
            <div className="grid grid-cols-3 gap-3">
              {pendingCalls.map(call => {
                const st = statusStyle(call.status);
                return (
                  <div
                    key={call.id}
                    className={`bg-white border rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow ${
                      call.status === 'Disputed' ? 'border-red-300 ring-1 ring-red-200' :
                      call.status === 'Call Issued' ? 'border-rose-300 ring-1 ring-rose-200' :
                      'border-amber-200'
                    }`}
                    onClick={() => setSelected(call)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-slate-900 text-sm">{call.counterparty}</div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                        {call.status}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-slate-900">${call.marginCall?.toFixed(1)}M</div>
                    <div className="text-xs text-slate-500 mt-1">{call.portfolio}</div>
                    {call.callDue && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-rose-600 font-medium">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Due: {call.callDue}
                      </div>
                    )}
                    <div className="text-xs text-slate-400 mt-1">{call.collateral} · {call.csa}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Table + Collateral */}
        <div className="flex gap-5">
          {/* Table */}
          <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">All Margin Positions</span>
              <span className="text-xs text-slate-400">{MARGIN_DATA.length} portfolios</span>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    {[
                      'Counterparty', 'Portfolio', 'IM Req $M', 'IM Post $M', 'VM Recv $M', 'VM Pay $M',
                      'Margin Call', 'Collateral', 'CSA', 'Status',
                    ].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MARGIN_DATA.map(m => {
                    const st = statusStyle(m.status);
                    const isSelected = selected?.id === m.id;
                    const imOk = m.im_posted >= m.im_required;
                    return (
                      <tr
                        key={m.id}
                        onClick={() => setSelected(m)}
                        className={`border-b border-slate-100 cursor-pointer transition-colors ${
                          isSelected ? 'bg-rose-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-3 py-3 font-medium text-slate-900 whitespace-nowrap">{m.counterparty}</td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{m.portfolio}</td>
                        <td className="px-3 py-3 font-mono text-slate-700">{m.im_required.toFixed(1)}</td>
                        <td className={`px-3 py-3 font-mono font-medium ${imOk ? 'text-emerald-700' : 'text-red-600'}`}>
                          {m.im_posted.toFixed(1)}
                        </td>
                        <td className="px-3 py-3 font-mono text-emerald-700">
                          {m.vm_receivable > 0 ? `+${m.vm_receivable.toFixed(1)}` : '—'}
                        </td>
                        <td className="px-3 py-3 font-mono text-red-600">
                          {m.vm_payable > 0 ? `-${m.vm_payable.toFixed(1)}` : '—'}
                        </td>
                        <td className="px-3 py-3 font-mono font-semibold">
                          {m.marginCall !== null ? (
                            <span className="text-rose-600">${m.marginCall.toFixed(1)}M</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{m.collateral}</td>
                        <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{m.csa}</td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                            {m.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Collateral Panel */}
          <div className="w-64 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-slate-700 mb-4">Collateral Breakdown</div>
              <div className="space-y-3">
                {COLLATERAL_BREAKDOWN.map(c => (
                  <div key={c.name}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-600">{c.name}</span>
                      <span className="font-semibold text-slate-800">{c.pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${c.color}`} style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-2">Total Collateral Held</div>
                <div className="text-xl font-bold text-rose-600">
                  ${MARGIN_DATA.reduce((s, m) => s + m.im_posted, 0).toFixed(1)}M
                </div>
                <div className="text-xs text-slate-400">Initial Margin only</div>
              </div>
            </div>

            {/* IM Gap */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-slate-700 mb-3">IM Coverage</div>
              {MARGIN_DATA.map(m => {
                const gap = m.im_posted - m.im_required;
                return (
                  <div key={m.id} className="flex items-center gap-2 mb-2">
                    <div className="flex-1 truncate text-xs text-slate-600">{m.counterparty}</div>
                    <div className={`text-xs font-semibold ${gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {gap >= 0 ? '+' : ''}{gap.toFixed(1)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, color, highlight,
}: {
  label: string; value: string; sub: string; color: string; highlight?: boolean;
}) {
  const colorMap: Record<string, string> = {
    rose: 'text-rose-700', blue: 'text-blue-700', red: 'text-red-700',
    green: 'text-emerald-700', amber: 'text-amber-700',
  };
  return (
    <div className={`bg-white border rounded-lg p-4 ${highlight ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200'}`}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${colorMap[color] ?? 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}